import React from 'react';
import { Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import { useQuery } from 'react-query';
import { CheckOutlined, DownOutlined, UserAddOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { contextAPI } from '../services/api';
import { CLIENT_BRAND } from '../config/brand';
import styles from './TenantSwitcher.module.css';

interface TenantSwitcherProps {
  collapsed?: boolean;
}

// Tenant card in the sidebar (under the logo): shows which organization the
// admin is operating in and switches between saved accounts. Replaces the old
// header label + the account entries that used to live in the user menu.
const TenantSwitcher: React.FC<TenantSwitcherProps> = ({ collapsed }) => {
  const navigate = useNavigate();
  const { user, sessions, switchAccount } = useAuth();

  // Same query key as AdminLayout — react-query dedupes, no extra request.
  const { data: tenantContext } = useQuery(['admin-context'], contextAPI.get, {
    retry: false,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const brandName = tenantContext?.platform.brand_name ?? CLIENT_BRAND;
  const slug = tenantContext?.platform.slug;
  // The org name is usually identical to the platform brand (FishSpot ›
  // FishSpot) — show it only when it actually adds information.
  const orgName = tenantContext?.organization?.name;
  const distinctOrgName = orgName && orgName !== brandName ? orgName : null;

  const items: MenuProps['items'] = [
    {
      type: 'group',
      label: 'Організації',
      children: sessions.map((s) => ({
        key: `account-${s.user.id}`,
        icon: (
          <span className={styles.itemAvatar}>
            {(s.orgName ?? s.user.username)?.charAt(0).toUpperCase()}
          </span>
        ),
        label: (
          <span className={styles.itemLabel}>
            <span className={styles.itemName}>{s.orgName ?? s.user.username}</span>
            {s.org && <span className={styles.itemSlug}>{s.org}</span>}
            {s.user.id === user?.id && <CheckOutlined className={styles.itemCheck} />}
          </span>
        ),
        onClick: () => switchAccount(s.user.id),
      })),
    },
    { type: 'divider' },
    {
      key: 'add-account',
      icon: <UserAddOutlined />,
      label: 'Додати акаунт',
      onClick: () => navigate('/login?add=1'),
    },
  ];

  return (
    <div className={styles.wrap}>
      <Dropdown menu={{ items }} trigger={['click']} placement="bottomLeft">
        <button
          type="button"
          className={`${styles.card} ${collapsed ? styles.cardCollapsed : ''}`}
          aria-label={`Організація: ${brandName}. Перемкнути організацію`}
          aria-haspopup="menu"
        >
          <span className={styles.avatar}>{brandName.charAt(0).toUpperCase()}</span>
          {!collapsed && (
            <>
              <span className={styles.meta}>
                <span className={styles.name}>
                  {brandName}
                  {distinctOrgName && <span className={styles.orgName}> · {distinctOrgName}</span>}
                </span>
                {slug && <span className={styles.slug}>{slug}</span>}
              </span>
              <DownOutlined className={styles.chevron} />
            </>
          )}
        </button>
      </Dropdown>
    </div>
  );
};

export default TenantSwitcher;
